import { type TypeTableColumn } from '@thefarmersfront/kitchen';

import { BadgeCell, ItemListCell, TextButtonCell } from '~/components/common';

import { maskName } from '~/utils/common';
import { formatDate } from '~/utils/date';

import { DATE_FORMAT } from '~/constants';
import { CLAIM_RETURN_STATUS_BADGE_COLOR, type TypeClaimReturnStatus } from '~/constants/claim';

import type { TypeReturnData } from '~/types/claim.types';

/**
 * [반품 신청/조회] 페이지 > 테이블 컬럼 정의
 *
 * @note 행 선택(단일)은 3PL 권한일 때만 No 칸 체크박스로 한다.
 */
const getClaimReturnColumnDefs = ({
  hasPermission3PL,
  onClickReturnReceiptId,
}: {
  hasPermission3PL: boolean;
  onClickReturnReceiptId: (returnReceiptId: string) => void;
}) => {
  const columnDefs: TypeTableColumn<TypeReturnData>[] = [
    {
      id: 'no',
      header: 'No',
      cell: ({ row }) => row.index + 1,
      size: 72,
      meta: { selection: hasPermission3PL },
    },
    {
      accessorKey: 'status',
      header: '반품상태',
      size: 100,
      cell: ({ getValue }) => {
        const value = getValue<TypeReturnData['status']>();
        if (!value?.code) return '-';
        const code = value.code as TypeClaimReturnStatus;
        return (
          <BadgeCell
            value={code}
            colorMap={{ [code]: { label: value.name, color: CLAIM_RETURN_STATUS_BADGE_COLOR[code] } }}
            variant="light"
            status
          />
        );
      },
    },
    {
      accessorKey: 'returnReceiptAt',
      header: '반품접수일시',
      size: 160,
      cell: ({ getValue }) => formatDate(getValue<string>(), DATE_FORMAT.DATE_TIME) ?? '-',
    },
    {
      accessorKey: 'clientOrderCode',
      header: '원판매처 주문번호',
      size: 180,
    },
    {
      accessorKey: 'returnReceiptId',
      header: '반품요청번호',
      size: 180,
      cell: ({ getValue, row }) => (
        <TextButtonCell value={getValue()} onClick={() => onClickReturnReceiptId(row.original.returnReceiptId)}>
          {row.original.returnReceiptId ?? '-'}
        </TextButtonCell>
      ),
    },
    {
      id: 'member.name',
      accessorFn: (data) => data.member?.name,
      header: '수령자명',
      size: 100,
      cell: ({ getValue }) => maskName(getValue<string>()) ?? '-',
    },
    {
      id: 'returnCourier.name',
      accessorFn: (data) => data.returnCourier?.name,
      header: '회수 배송사',
      size: 88,
    },
    {
      accessorKey: 'returnOrderTrackings',
      header: '회수 송장번호',
      minSize: 280,
      meta: { flex: true },
      cell: ({ getValue }) => {
        const value = getValue<TypeReturnData['returnOrderTrackings']>();
        if (!value || value.length === 0) return '-';
        return value.length > 1 ? `${value[0].invoiceNumber} 외 ${value.length - 1}개` : value[0].invoiceNumber;
      },
    },
    {
      accessorKey: 'outboundOrderCode',
      header: '원출고요청번호',
      size: 180,
    },
    {
      id: 'returnOrderItems',
      header: '반품 상품명',
      minSize: 90,
      size: 240,
      cell: ({ row }) => <ItemListCell value={row.original.returnOrderItems} fieldName="goodsName" />,
    },
    {
      accessorKey: 'updatedAt',
      header: '최근 업데이트일시',
      size: 159,
      cell: ({ getValue }) => formatDate(getValue<string>(), DATE_FORMAT.DATE_TIME) ?? '-',
    },
  ];

  return { columnDefs };
};

export default getClaimReturnColumnDefs;
